import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import { Edit, Close, Delete, RestoreFromTrash, WhatsApp, KeyOutlined } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  whatsappChannelsService,
  WhatsAppChannel,
} from "../services/whatsappChannelsService";
import { crmService, Pipeline } from "../services/crmService";
import { colors, typography, pageHeader } from "../theme/designSystem";

// Canais de WhatsApp da entidade (números na Meta Cloud API). O token NUNCA passa
// pelo navegador: a tela registra apenas o NOME da variável de ambiente que o contém.

const NAME_MAX = 100;
const PNID_MAX = 50;
const WABA_MAX = 50;
const PHONE_MAX = 20;
const TOKEN_ENV_MAX = 100;

const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    "&.Mui-focused": { color: colors.primary },
  },
};

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: "white",
  width: { xs: 44, sm: 32 },
  height: { xs: 44, sm: 32 },
  "&:hover": { backgroundColor: hover },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
});

interface ChannelForm {
  id: string;
  name: string;
  phoneNumberId: string;
  wabaId: string;
  displayPhone: string;
  defaultPipelineId: string;
  tokenEnvVar: string;
}

const emptyForm: ChannelForm = {
  id: "",
  name: "",
  phoneNumberId: "",
  wabaId: "",
  displayPhone: "",
  defaultPipelineId: "",
  tokenEnvVar: "",
};

const WhatsAppChannels: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";

  const [channels, setChannels] = useState<WhatsAppChannel[]>([]);
  const [search, setSearch] = useState("");
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ChannelForm>(emptyForm);
  const [confirmToggle, setConfirmToggle] = useState<WhatsAppChannel | null>(null);

  const load = useCallback(async () => {
    if (!entityId) {
      setError("Sessão sem entidade definida. Refaça o login para continuar.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [channelList, pipelineList] = await Promise.all([
        whatsappChannelsService.getByEntity(entityId, true),
        crmService.getPipelines(entityId, false),
      ]);
      setChannels(channelList);
      setPipelines(pipelineList);
    } catch {
      setError("Não foi possível carregar os canais. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  /** Minusculas e sem acento: quem busca "recepcao" espera achar "Recepção". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  // A busca olha o nome E o numero: quem procura um canal lembra de um ou do outro.
  const filtered = channels.filter((c) => {
    if (!search.trim()) {
      return true;
    }
    const alvo = normalize([c.name, c.displayPhone].filter(Boolean).join(" "));
    return alvo.includes(normalize(search));
  });

  const clearFilters = () => setSearch("");
  const hasFilterChanges = search !== "";

  useEffect(() => {
    load();
  }, [load]);

  const pipelineName = useMemo(() => {
    const map = new Map(pipelines.map((p) => [p.id, p.name]));
    return (id: string | null) => (id ? map.get(id) || "—" : "Funil padrão");
  }, [pipelines]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (channel: WhatsAppChannel) => {
    setForm({
      id: channel.id,
      name: channel.name,
      phoneNumberId: channel.phoneNumberId,
      wabaId: channel.wabaId || "",
      displayPhone: channel.displayPhone || "",
      defaultPipelineId: channel.defaultPipelineId || "",
      tokenEnvVar: channel.tokenEnvVar || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      showToast("Informe o nome do canal.", "error");
      return;
    }
    if (!form.id && !form.phoneNumberId.trim()) {
      showToast("Informe o phone_number_id da Meta.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        wabaId: form.wabaId.trim() || null,
        displayPhone: form.displayPhone.trim() || null,
        defaultPipelineId: form.defaultPipelineId || null,
        tokenEnvVar: form.tokenEnvVar.trim() || null,
      };
      if (form.id) {
        await whatsappChannelsService.update(form.id, entityId, payload);
        showToast("Canal atualizado.", "success");
      } else {
        await whatsappChannelsService.create({
          entityId,
          phoneNumberId: form.phoneNumberId.trim(),
          ...payload,
        });
        showToast("Canal criado. O webhook já roteia mensagens deste número.", "success");
      }
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível salvar o canal.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!confirmToggle) return;
    const channel = confirmToggle;
    setConfirmToggle(null);
    setSaving(true);
    try {
      await whatsappChannelsService.setActive(channel.id, entityId, !channel.active);
      showToast(channel.active ? "Canal inativado." : "Canal reativado.", "success");
      await load();
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível alterar o canal.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Canais de WhatsApp
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Números da entidade conectados à API oficial da Meta. Mensagem recebida em um
                canal cria o lead no funil configurado. O token de acesso fica no servidor — aqui
                registra-se apenas o nome da variável que o contém.
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome ou número"
                  placeholder="Buscar canal"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Incluir canal" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={channels.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary }} />
            </Box>
          ) : error ? (
            <Paper elevation={0} sx={{ ...filterCardSx, textAlign: "center", color: colors.error }}>
              {error}
            </Paper>
          ) : filtered.length === 0 ? (
            <Paper elevation={0} sx={{ ...filterCardSx, textAlign: "center" }}>
              <WhatsApp sx={{ fontSize: 40, color: colors.whatsapp, mb: 1 }} />
              <Typography sx={{ fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                Nenhum canal cadastrado
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                Cadastre o número da clínica criado na Meta (phone_number_id) para o funil
                começar a receber leads pelo WhatsApp.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {filtered.map((channel) => (
                <Paper
                  key={channel.id}
                  elevation={0}
                  sx={{
                    ...filterCardSx,
                    mb: 0,
                    padding: "1rem 1.25rem",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                    opacity: channel.active ? 1 : 0.6,
                  }}
                >
                  <WhatsApp sx={{ color: colors.whatsapp, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
                    <Typography sx={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
                      {channel.name}
                    </Typography>
                    <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                      {channel.displayPhone || "número não informado"} · phone_number_id:{" "}
                      {channel.phoneNumberId}
                    </Typography>
                    <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                      Funil de destino: {pipelineName(channel.defaultPipelineId)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Tooltip
                      title={
                        channel.tokenEnvVar
                          ? `Token pela env ${channel.tokenEnvVar}`
                          : "Sem chave de token configurada — envio de mensagens bloqueado"
                      }
                    >
                      <Chip
                        size="small"
                        icon={<KeyOutlined />}
                        label={channel.tokenEnvVar ? "Token configurado" : "Sem token"}
                        sx={{
                          backgroundColor: channel.tokenEnvVar ? "#e8f5e9" : "#fff3cd",
                          color: channel.tokenEnvVar ? colors.success : "#856404",
                        }}
                      />
                    </Tooltip>
                    {!channel.active && (
                      <Chip size="small" label="Inativo" sx={{ backgroundColor: "#e9ecef" }} />
                    )}
                    <Tooltip title="Editar canal">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(channel)}
                        sx={rowActionSx(colors.primary, colors.primaryHover)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={channel.active ? "Inativar canal" : "Reativar canal"}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmToggle(channel)}
                        sx={
                          channel.active
                            ? rowActionSx(colors.error, "#c82333")
                            : rowActionSx(colors.success, "#218838")
                        }
                      >
                        {channel.active ? (
                          <Delete fontSize="small" />
                        ) : (
                          <RestoreFromTrash fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          </div>
        </Container>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { position: "relative", overflow: "hidden", borderRadius: "12px", maxHeight: "85vh" } }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form.id ? "Editar canal" : "Novo canal de WhatsApp"}
          </Typography>
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "1rem !important" }}>
          <TextField
            label="Nome do canal"
            placeholder="Ex.: Comercial, Recepção"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NAME_MAX }}
          />
          <TextField
            label="phone_number_id (Meta)"
            placeholder="Identificador do número no painel da Meta"
            value={form.phoneNumberId}
            onChange={(e) => setForm({ ...form, phoneNumberId: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: PNID_MAX }}
            disabled={!!form.id}
            helperText={
              form.id
                ? "O phone_number_id não muda — para trocar o número, crie outro canal."
                : "Meta for Developers > app do WhatsApp > API Setup."
            }
          />
          <TextField
            label="Número exibido (com DDI)"
            placeholder="5511999998888"
            value={form.displayPhone}
            onChange={(e) => setForm({ ...form, displayPhone: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: PHONE_MAX }}
          />
          <TextField
            label="WABA ID (opcional)"
            value={form.wabaId}
            onChange={(e) => setForm({ ...form, wabaId: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: WABA_MAX }}
          />
          <TextField
            select
            label="Funil de destino dos leads"
            value={form.defaultPipelineId}
            onChange={(e) => setForm({ ...form, defaultPipelineId: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            helperText="Sem escolha, os leads vão para o funil padrão da entidade."
          >
            <MenuItem value="">Funil padrão da entidade</MenuItem>
            {pipelines.map((pipeline) => (
              <MenuItem key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
                {pipeline.isDefault ? " (padrão)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Nome da variável de ambiente do token"
            // a variável no servidor usa "__", mas o .NET a converte para ":" ao carregar:
            // digitar underline aqui faz o envio falhar com "token ausente", sem pista na tela
            placeholder="Ex.: Whatsapp:ChannelToken:t000001"
            value={form.tokenEnvVar}
            onChange={(e) => setForm({ ...form, tokenEnvVar: e.target.value })}
            fullWidth
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: TOKEN_ENV_MAX }}
            helperText="Separe com dois-pontos, não underline (a variável Whatsapp__ChannelToken__t000001 vira Whatsapp:ChannelToken:t000001). O token em si é configurado no servidor pelo suporte técnico — nunca é colado aqui."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primaryHover },
            }}
          >
            {form.id ? "Salvar" : "Criar canal"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!confirmToggle}
        title={confirmToggle?.active ? "Inativar canal" : "Reativar canal"}
        message={
          confirmToggle?.active
            ? `Inativar "${confirmToggle?.name}"? Mensagens recebidas neste número deixarão de criar leads até a reativação.`
            : `Reativar "${confirmToggle?.name}"? O número volta a receber leads pelo webhook.`
        }
        type={confirmToggle?.active ? "danger" : "info"}
        onConfirm={toggleActive}
        onCancel={() => setConfirmToggle(null)}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <FooterInternal />
    </Box>
  );
};

export default WhatsAppChannels;
